import React, { ChangeEvent, FC, HTMLAttributes, useState } from 'react';
import { Autocomplete, AutocompleteRenderOptionState, TextField } from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { useTranslation } from 'react-i18next';

import {
  useGetUsersByEmailAddressQuery,
  AdvancedFilters,
  useGetClientPageMappingsQuery,
  UserProfile,
} from 'api';
import { MAX_ADVANCED_FILTER_QUERIES } from 'app-constants';
import { CompaniesFilter, PeopleFilter } from 'features/insights/types';
import { useDebounce } from 'use-debounce';
import { AdvancedFilterFunctions } from 'features/insights/components/utils';
import { useProtectedClient } from 'hooks';

interface IProps {
  tempFilter: PeopleFilter | CompaniesFilter;
  setTempFilter: (filter: PeopleFilter | CompaniesFilter) => void;
  filterField: string;
}

const AdvancedFilterInput: FC<IProps> = ({ tempFilter, setTempFilter, filterField }) => {
  const client = useProtectedClient();
  const { t } = useTranslation();
  const hasReachedMaxQuery = tempFilter[filterField]?.length >= MAX_ADVANCED_FILTER_QUERIES;
  const [searchFilterField, setSearchFilterField] = useState('');
  const [debouncedSearchFilterField] = useDebounce(searchFilterField, 250);

  const queryOptions = {
    [AdvancedFilters.Users]: useGetUsersByEmailAddressQuery(
      {
        email_address_list: debouncedSearchFilterField,
      },
      {
        enabled: debouncedSearchFilterField.length > 2 && filterField == AdvancedFilters.Users,
      }
    ),
    [AdvancedFilters.CompanyPages]: useGetClientPageMappingsQuery(
      {
        id: client.id,
      },
      {
        enabled: filterField == AdvancedFilters.CompanyPages,
      }
    ),
  };

  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  const options = AdvancedFilterFunctions[filterField].getOptions(queryOptions[filterField]);
  const selectedOptions = AdvancedFilterFunctions[filterField].getSelectedOptions(
    tempFilter[filterField]
  );

  function handleSearchOnChange(
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) {
    setSearchFilterField(event.target.value);
  }

  return (
    <Autocomplete
      options={options}
      freeSolo
      multiple
      disableCloseOnSelect
      renderTags={AdvancedFilterFunctions[filterField].renderTags}
      renderOption={(
        props: HTMLAttributes<HTMLLIElement>,
        option: UserProfile,
        { selected }: AutocompleteRenderOptionState
      ) => {
        return AdvancedFilterFunctions[filterField].renderOption(
          props,
          option,
          selected,
          icon,
          checkedIcon
        );
      }}
      getOptionLabel={AdvancedFilterFunctions[filterField].renderOptionLabel}
      value={selectedOptions}
      isOptionEqualToValue={AdvancedFilterFunctions[filterField].isOptionEqualToValue}
      inputValue={searchFilterField}
      onChange={(event: ChangeEvent, nextValue: []) =>
        AdvancedFilterFunctions[filterField].handleOnChange(event, nextValue, {
          tempFilter,
          setTempFilter,
          setSearchFilterField,
        })
      }
      renderInput={params => (
        <TextField
          placeholder={
            tempFilter[filterField]?.length
              ? null
              : t(`insights:advanced_filter_placeholder_${filterField}`)
          }
          variant="outlined"
          error={hasReachedMaxQuery}
          onChange={handleSearchOnChange}
          helperText={hasReachedMaxQuery ? t('insights:max_advanced_filter_query_error') : ''}
          {...params}
        />
      )}
    />
  );
};

export default AdvancedFilterInput;
